import store from '@/store'

export function isModuleEnabled(moduleSlug) {
  if (!moduleSlug) return false
  const normalized = moduleSlug.replace(/_/g, '-')
  const underscore = moduleSlug.replace(/-/g, '_')
  const camelCase = moduleSlug.replace(/-([a-z])/g, (g) => g[1].toUpperCase())
  const kebabCase = moduleSlug.replace(/([A-Z])/g, '-$1').toLowerCase()

  return (
    store.getters.isModuleEnabled(moduleSlug) ||
    store.getters.isModuleEnabled(normalized) ||
    store.getters.isModuleEnabled(underscore) ||
    store.getters.isModuleEnabled(camelCase) ||
    store.getters.isModuleEnabled(kebabCase)
  )
}

export function hasUserModuleAccess(moduleSlug) {
  if (!moduleSlug) return true
  if (!isModuleEnabled(moduleSlug)) return false

  const userPermissions = store.getters.userPermissions || []
  if (userPermissions.length === 0) return true

  const modulesBySlug = store.getters.modulesBySlug || {}
  const slugVariants = [
    moduleSlug,
    moduleSlug.replace(/_/g, '-'),
    moduleSlug.replace(/-/g, '_')
  ]
  const moduleData = slugVariants.map((slug) => modulesBySlug[slug]).find(Boolean)

  if (!moduleData) return true

  return userPermissions.includes(moduleData.id)
}

export function isTenantInGroup(moduleSlug) {
  const availableModules = store.getters.availableModules
  return availableModules.includes(moduleSlug)
}
export function getEnabledModules() {
  return store.getters.availableModules
}

export function getTenantConfig() {
  const storedConfig = store.getters.tenantConfig
  if (storedConfig && Object.keys(storedConfig).length > 0) {
    return storedConfig
  }

  return {}
}

export const filterEnabledArrayItems = (value) => {
  if (!Array.isArray(value)) {
    return value
  }

  const containsObjects = value.some(
    (item) =>
      typeof item === 'object' &&
      item !== null &&
      !Array.isArray(item) &&
      Object.keys(item).length > 0 &&
      Object.keys(item).some((k) => !['0', '1', '2'].includes(String(k)))
  )

  if (containsObjects) {
    return value.filter((item) => {
      if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
        return item.enabled !== false
      }
      return true
    })
  }

  return value
}

export function isTenantConfigFlagEnabled(key) {
  const fromUsers = store.getters.getModuleConfig?.('users', key, undefined)
  const fromFlat = getTenantConfigValue(key, undefined)
  const raw = fromUsers !== undefined && fromUsers !== null ? fromUsers : fromFlat

  return raw === true || raw === 1 || raw === '1' || raw === 'true'
}

export function getTenantConfigValue(key, defaultValue = null) {
  const config = getTenantConfig()
  const value = config[key] !== undefined ? config[key] : defaultValue

  if (Array.isArray(value) && value.length > 0) {
    const containsObjectsWithCode = value.some(
      (item) => typeof item === 'object' && item !== null && !Array.isArray(item) && item.code !== undefined
    )

    if (containsObjectsWithCode) {
      return value
        .filter((item) => item.enabled !== false)
        .map((item) => (typeof item === 'object' && item.code ? item.code : item))
    }
  }

  return value
}

export function getTenantId() {
  return store.getters.tenantId
}

export function isTenancyEnabled() {
  return store.getters.tenancyEnabled
}

export function getModuleDetails(moduleSlug) {
  const modules = store.getters.tenantModules
  return modules[moduleSlug] || null
}

export function isInLegacyGroup(groupName) {
  const tenantId = getTenantId()
  if (!tenantId) return false

  // Legacy group mapping - only for backward compatibility with old routes
  // New routes should use moduleSlug directly in route meta instead of allowedTenant
  const legacyGroupMapping = {
    pointsGroup: 'points-list', // POINTS_LIST in USER
    pointsManagementGroup: 'points-management', // 'points' management / booster
    trainingGroup: 'trainings', // TRAININGS
    bTrainingGroup: 'b-trainings', // B_TRAININGS
    surveyGroup: 'surveys', // custom from allModules → 'surveys'
    newsFeedGroup: 'news-feed', // NEWS_FEED
    mediaPagesGroup: 'media', // custom 'media' from allModules
    bannerGroup: 'default-banners', // DEFAULT_BANNERS
    pricesGroup: 'prices', // PRICES
    productsGroup: 'products', // PRODUCTS
    vendorsGroup: 'vendors', // VENDORS
    promotions: 'sales-promotions', // SALES_PROMOTIONS
    campaigns: 'campaigns', // custom from allModules → 'campaigns'
    vouchers: 'vouchers', // custom 'vouchers' from allModules
    wholesalerManagement: 'wholesaler-management', // WHOLESALER_MANAGEMENT
    hunter: 'hunters', // HUNTERS
    investSupport: 'investments-request', // INVESTMENTS_REQUEST
    externalTrainings: 'external-trainings-request', // EXTERNAL_TRAININGS_REQUEST
    customerGroup: 'customer-groups', // custom from allModules → 'customer-groups'
    pointBoosterGroup: 'booster', // BOOSTER
    requestsGroup: 'product-trainings-request', // PRODUCT_TRAININGS_REQUEST
    mainBoxesGroup: 'main-boxes', // MAIN_BOXES
    uberAll: 'uberall', // custom 'uberall' from allModules
    bankStatement: 'bank-statement', // custom from allModules → 'bank-statement'
    aboutPage: 'about', // ABOUT
    contactService: 'contact-service', // CONTACT_SERVICE
    brochurePricesGroup: 'brochure-prices', // custom from allModules → 'brochure-prices'
    priceCategoryGroup: 'price-categories', // PRICE_CATEGORIES
    pointsExporterGroup: 'points-importer', // POINTS_IMPORTER
    approveIndirect: 'approve-indirect', // custom from allModules → 'approve-indirect'
    boosterCCS: 'booster-ccs', // custom from allModules → 'booster-ccs'
    pointsImporter: 'points-importer', // POINTS_IMPORTER
    customerNumbersAssistant: 'customer-numbers-assistant', // custom from allModules
    customerNumbersToApprove: 'customer-numbers-to-approve', // custom from allModules
    inactiveUsers: 'in-active-cs-nr' // IN_ACTIVE_CS_NR
  }

  const moduleSlug = legacyGroupMapping[groupName]
  if (!moduleSlug) {
    console.warn(`Unknown legacy group: ${groupName}`)
    return false
  }

  return isModuleEnabled(moduleSlug)
}

export default {
  isModuleEnabled,
  hasUserModuleAccess,
  isTenantInGroup,
  getEnabledModules,
  getTenantConfig,
  getTenantConfigValue,
  isTenantConfigFlagEnabled,
  getTenantId,
  isTenancyEnabled,
  getModuleDetails,
  isInLegacyGroup,
  filterEnabledArrayItems
}
